import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { PourbackReason } from "@/pages/caster/models/catalogs/pourback-reasons";
import { useModalStyles } from "@/pages/laboratory/styles/modalStyles";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid2 as Grid,
	Snackbar,
	TextField,
} from "@mui/material";
import { Alert } from "@mui/material";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { deletePourbackReason, upsertPourbackReason } from "../repositories";

type Props = {
	open: boolean;
	mode: "create" | "edit" | "delete";
	pourbackReason?: PourbackReason | null;
	onClose: () => void;
	onSuccess: () => void;
};

export const UpsertPourbackReason: React.FC<Props> = ({
	open,
	mode,
	pourbackReason,
	onClose,
	onSuccess,
}) => {
	const classes = useModalStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [error, setError] = useState("");
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<PourbackReason>({
		defaultValues: {
			PourbackReasonId: 0,
			PourbackReasonName: "",
			IsActive: true,
		},
	});

	const title = useMemo(() => {
		if (mode === "edit") {
			return "EDIT POURBACK REASON";
		} else if (mode === "delete") {
			return "DELETE POURBACK REASON";
		} else if (mode === "create") {
			return "NEW POURBACK REASON";
		}
	}, [mode]);

	const handleSnackbarClose = (
		event: Event | SyntheticEvent<Element, Event>,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarOpen(false);
	};

	const deletePourbackReasonAction = async (value?: string) => {
		if (value !== "OK") {
			setOpenDeleteDialog(false);
			return;
		}
		setOpenDeleteDialog(false);
		setIsSubmitLoading(true);

		const response = await deletePourbackReason(
			pourbackReason?.PourbackReasonId || 0,
		);

		if (response.ok) {
			setSnackbarMessage("The pourback reason was deleted successfully");
			setSnackbarOpen(true);
			onSuccess();
			onClose();
		} else {
			setError(response.message);
		}
		setIsSubmitLoading(false);
	};

	const onSubmit: SubmitHandler<PourbackReason> = async (data: any) => {
		setIsSubmitLoading(true);

		if (mode === "delete") {
			setOpenDeleteDialog(true);
		} else {
			const response = await upsertPourbackReason(data);
			if (response.ok) {
				setSnackbarMessage(
					mode === "edit"
						? "The pourback reason was updated successfully"
						: "The pourback reason was created successfully",
				);
				setSnackbarOpen(true);
				onSuccess();
				onClose();
			} else {
				setError(response.message);
			}
		}

		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (mode === "edit" || mode === "create" || mode === "delete") {
			if (pourbackReason) {
				reset(pourbackReason);
			} else if (mode === "create") {
				reset({
					PourbackReasonId: 0,
					PourbackReasonName: "",
					IsActive: true,
				});
			}
		}
	}, [mode, pourbackReason, reset]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={onClose}
				maxWidth="md"
				aria-labelledby="contained-modal-title-vcenter"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						<Grid container spacing={1}>
							<Grid size={{ xs: 12, md: 12 }}>
								<TextField
									{...register("PourbackReasonName", { required: true })}
									label="Pourback Reason Name"
									variant="outlined"
									error={!!errors.PourbackReasonName}
									fullWidth
									margin="dense"
									autoComplete="off"
									disabled={mode === "delete"}
								/>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={!!field.value}
											onCheckboxChange={field.onChange}
											label="Enabled"
											className={classes.checkbox}
											disabled={mode === "delete"}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={onClose}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color={mode === "delete" ? "error" : "primary"}
									type="submit"
								>
									{mode === "delete" ? "Delete" : "Save"}
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<ConfirmationDialogRaw
				title="Confirmation"
				message={`Are you sure you want to delete permanently the pourback reason: ${pourbackReason?.PourbackReasonName}?`}
				open={openDeleteDialog}
				onClose={deletePourbackReasonAction}
			/>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={2500}
				onClose={handleSnackbarClose}
			>
				<Alert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleSnackbarClose}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
